import EmojiPicker from "emoji-picker-react";
import fixWebmDuration from "fix-webm-duration";
import { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import { S3 } from "../../../../constants";
import useClickOutside from "../../../../hooks/useClickOutside";
import Modal from "../../../../parts/modal-new";
import SvgIcon from "../../../../parts/SvgIcon";
import { RillContext } from "../../../../rill/RillReact/share/base";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import FilterChips from "../../../athlete-portal/calendar/FilterChips";

const MAX_ATTACHMENTS = 10;
const MAX_ATTACHMENT_SIZE_MB = 25;
const MAX_ATTACHMENT_SIZE = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;
const MAX_VOICE_SECONDS = 5 * 60;
const MAX_CONFIRM_CHIPS = 12;

/**
 * Same shape/dedupe as AssignSingleWorkoutClients.js and SendModal.js's local helper of the
 * same name (duplicated per-file by design, not shared).
 */
function parseOrganizationAthletesToOptions(allAthletesFromOrganization) {
  const options = [];
  const uniqueAthleteIds = new Set();
  (allAthletesFromOrganization || [])
    .filter(({ athleteStatus }) => !athleteStatus?.inactive)
    .forEach(({ athleteId, athleteName, athleteGroups, athleteAvatar }) => {
      if (!uniqueAthleteIds.has(athleteId)) {
        uniqueAthleteIds.add(athleteId);
        options.push({
          value: athleteId,
          label: athleteName,
          groups: athleteGroups || [],
          avatar: athleteAvatar,
        });
      }
    });
  return options;
}

/**
 * Same recipient-selection data shape as TeamInviteClients' getInitialOptions
 * (js/views/coaching-portal/teams/components/TeamInviteClients.js): "my active clients"
 * always available, "all organization clients" for Owner/Admin/Team Coach. No
 * existingMemberIds filter here — every active client is a valid mass-message recipient.
 *
 * Team Coach added literally rather than migrated to canViewAllOrganizationClients: that
 * predicate also includes Nutritionist, which send-mass-message.js's own
 * getAuthorizedRecipientIds deliberately does not — matching it here keeps the GUI option in
 * sync with what the API actually authorizes, instead of showing an "all clients" picker a
 * Nutritionist would get a 403 for using.
 *
 * A Team Coach has no roster of their own, so their "athletesOptions" IS the org-wide list
 * from the start (same pattern as AssignSingleWorkoutClients.js/SendModal.js) — showing an
 * empty list behind a separate "All Organization" checkbox they'd have to know to tick would
 * mean the modal opens empty for the one role it's most useful for.
 */
function getInitialOptions(locals) {
  const { activeUser: { athletes } = {}, allAthletesFromOrganization } = locals;

  const isAllowToViewAllOrganizationsAthletes =
    locals.organizationRoleName &&
    (locals.organizationRoleName === "Owner" ||
      locals.organizationRoleName === "Admin" ||
      locals.organizationRoleName === "Team Coach");
  const isTeamCoach = locals.organizationRoleName === "Team Coach";

  const referAsClients = Boolean(locals.activeUser?.referAsClients);

  let athletesOptions;
  if (isTeamCoach) {
    athletesOptions = parseOrganizationAthletesToOptions(
      allAthletesFromOrganization
    );
  } else {
    athletesOptions = (athletes || [])
      .filter(({ status }) => !status?.inactive)
      .map((athlete) => ({
        value: athlete._id,
        label: athlete.name,
        groups: athlete.groups || [],
        avatar: athlete.avatar,
      }));
  }

  const allAthletesFromOrganizationOptions =
    allAthletesFromOrganization &&
    isAllowToViewAllOrganizationsAthletes &&
    !isTeamCoach
      ? parseOrganizationAthletesToOptions(allAthletesFromOrganization)
      : [];

  return {
    athletesOptions,
    allAthletesFromOrganizationOptions,
    isAllowToViewAllOrganizationsAthletes:
      isAllowToViewAllOrganizationsAthletes && !isTeamCoach,
    referAsClients,
  };
}

function getImgUrl(athlete) {
  if (athlete.avatar) {
    return `${S3.PATH}${athlete.avatar.s3Path}`;
  }
  return "/img/new-icons/user-circle.svg";
}

function firstNameOf(fullName = "") {
  return fullName.trim().split(/\s+/)[0] || "";
}

function personalizeForPreview(text, firstName, fallbackName) {
  const name = firstName || fallbackName;
  return text.replace(/\{first_name\}/gi, name);
}

function pickAudioMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];
  return candidates.find((type) => MediaRecorder.isTypeSupported?.(type)) || "";
}

function formatSeconds(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export const MassMessageModalComponent = () => {
  const { t } = useTranslation(["coaching-portal", "common"]);
  const context = useContext(RillContext);
  const locals = context?.locals || {};
  const { activeUser = {}, organizationOwner, organizationRoleName } = locals;
  const webAppTheme = activeUser.webAppTheme || "dark";

  const initialOptions = useMemo(() => getInitialOptions(locals), [locals]);
  const referAsClients = initialOptions.referAsClients;

  const [state, setState] = useState({
    selectedAthletes: [],
    athletesOptions: initialOptions.athletesOptions,
    allAthletesFromOrganizationOptions:
      initialOptions.allAthletesFromOrganizationOptions,
    isAllowToViewAllOrganizationsAthletes:
      initialOptions.isAllowToViewAllOrganizationsAthletes,
  });
  const {
    selectedAthletes,
    athletesOptions,
    allAthletesFromOrganizationOptions,
    isAllowToViewAllOrganizationsAthletes,
    allAthletes,
    allOrganizationAthletes,
  } = state;

  // A Team Coach has no groups of their own — fall back to the Owner's, same
  // stand-in used throughout the Athletes list and group editing.
  const athletesGroups =
    organizationRoleName === "Team Coach"
      ? organizationOwner?.athletesGroups
      : activeUser.athletesGroups;

  const [step, setStep] = useState("compose");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachError, setAttachError] = useState(null);
  const [voiceNote, setVoiceNote] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [micError, setMicError] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const textareaRef = useRef(null);
  const emojiPickerRef = useRef(null);
  const emojiButtonRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  const recordingStartRef = useRef(0);
  const recordingTimerRef = useRef(null);

  useClickOutside(
    emojiPickerRef,
    () => setShowEmojiPicker(false),
    showEmojiPicker,
    emojiButtonRef.current ? [emojiButtonRef.current] : []
  );

  const getFiltersToClear = useCallback(
    (prev) => {
      const isActive =
        prev.allAthletes ||
        prev.allOrganizationAthletes ||
        (athletesGroups?.some((elem) => prev[elem.name]) ?? false);
      if (!isActive) return {};
      const filters = { allAthletes: false, allOrganizationAthletes: false };
      if (athletesGroups) {
        athletesGroups.forEach((elem) => {
          filters[elem.name] = false;
        });
      }
      return filters;
    },
    [athletesGroups]
  );

  const searchAthletes = useCallback(
    (value) => {
      const label = value.toString().toLowerCase();
      setState((prev) => {
        const originalList =
          prev.allAthletesFromOrganizationOptions.length > 0
            ? [...prev.allAthletesFromOrganizationOptions].sort((a, b) =>
                a.label.localeCompare(b.label)
              )
            : initialOptions.athletesOptions;

        const filteredArray = originalList.filter((item) =>
          item.label.toLowerCase().startsWith(label)
        );

        return {
          ...prev,
          athletesOptions: value ? filteredArray : originalList,
        };
      });
    },
    [initialOptions.athletesOptions]
  );

  const handleInputChange = useCallback((value, name) => {
    setState((prev) => {
      const {
        selectedAthletes: prevSelected,
        athletesOptions: prevOptions,
        allAthletesFromOrganizationOptions: prevOrgOptions,
        isAllowToViewAllOrganizationsAthletes: prevAllowOrg,
      } = prev;
      let selection = [...prevSelected];

      const currentCoachAthletes =
        prevOrgOptions.length > 0 && prevAllowOrg
          ? prevOrgOptions.filter((athlete) =>
              prevOptions.some((option) => option.value === athlete.value)
            )
          : prevOptions;

      if (name === "allAthletes" && value) {
        selection = currentCoachAthletes;
      } else if (
        (name === "allAthletes" || name === "allOrganizationAthletes") &&
        !value
      ) {
        selection = [];
      } else if (name === "allOrganizationAthletes" && value) {
        selection = prevOrgOptions;
      } else if (value) {
        selection =
          prevOrgOptions.length > 0 && prevAllowOrg
            ? [
                ...selection,
                ...prevOrgOptions.filter((elem) => elem.groups.includes(name)),
              ]
            : [
                ...selection,
                ...prevOptions.filter((elem) => elem.groups.includes(name)),
              ];
      } else {
        selection = selection.filter((elem) => !elem.groups.includes(name));
      }

      const uniqueSelection = selection.filter(
        (elem, index, arr) =>
          arr.findIndex((other) => other.value === elem.value) === index
      );

      return {
        ...prev,
        [name]: value,
        selectedAthletes: uniqueSelection,
      };
    });
  }, []);

  const handleAthleteChange = useCallback(
    (recipients) => {
      if (recipients.length === 0) {
        setState((prev) => ({
          ...prev,
          selectedAthletes: [],
          ...getFiltersToClear(prev),
        }));
      } else {
        setState((prev) => ({ ...prev, selectedAthletes: recipients }));
      }
    },
    [getFiltersToClear]
  );

  const handleClearAll = useCallback(() => {
    setState((prev) => ({
      ...prev,
      selectedAthletes: [],
      ...getFiltersToClear(prev),
    }));
  }, [getFiltersToClear]);

  const generateAthleteListItem = useCallback(
    (athlete) => {
      const isSelected = selectedAthletes.some(
        (item) => item.value === athlete.value
      );
      const onClick = () =>
        isSelected
          ? handleAthleteChange(
              selectedAthletes.filter((ath) => ath.value !== athlete.value)
            )
          : handleAthleteChange([...selectedAthletes, athlete]);
      return (
        <li key={athlete.value} onClick={onClick}>
          <img
            src={
              isSelected ? "/img/new-icons/tick-circle.svg" : getImgUrl(athlete)
            }
            alt=""
            style={
              !athlete.avatar && !isSelected
                ? { backgroundColor: "var(--background-color)", padding: "8px" }
                : {}
            }
          />
          <span className="athlete-select-label" title={athlete.label}>
            {athlete.label}
          </span>
        </li>
      );
    },
    [selectedAthletes, handleAthleteChange]
  );

  // ---- Composer ----

  const insertAtCaret = (snippet) => {
    const el = textareaRef.current;
    if (!el) {
      setMessage((prev) => `${prev}${snippet}`);
      return;
    }
    const start = el.selectionStart ?? el.value.length;
    const end = el.selectionEnd ?? el.value.length;
    setMessage((prev) => `${prev.slice(0, start)}${snippet}${prev.slice(end)}`);
    requestAnimationFrame(() => {
      el.focus();
      const cursor = start + snippet.length;
      el.setSelectionRange(cursor, cursor);
    });
  };

  const handleAttachFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;
    setAttachError(null);

    if (attachments.length + incoming.length > MAX_ATTACHMENTS) {
      setAttachError(
        t("chat.massMessage.tooManyFiles", { max: MAX_ATTACHMENTS })
      );
      return;
    }
    const oversized = incoming.find((file) => file.size > MAX_ATTACHMENT_SIZE);
    if (oversized) {
      setAttachError(
        t("chat.massMessage.fileTooLarge", { max: MAX_ATTACHMENT_SIZE_MB })
      );
      return;
    }

    setAttachments((prev) => [
      ...prev,
      ...incoming.map((file) => ({
        file,
        url: file.type.startsWith("image/") ? URL.createObjectURL(file) : null,
      })),
    ]);
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const next = [...prev];
      const [removed] = next.splice(index, 1);
      if (removed?.url) URL.revokeObjectURL(removed.url);
      return next;
    });
  };

  const startRecording = async () => {
    setMicError(null);
    if (!navigator.mediaDevices?.getUserMedia) {
      setMicError(t("chat.massMessage.micUnsupported"));
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = pickAudioMimeType();
      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      recordedChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordedChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        const rawBlob = new Blob(recordedChunksRef.current, {
          type: recorder.mimeType || "audio/webm",
        });
        const durationMs = Math.max(
          1000,
          Date.now() - recordingStartRef.current
        );
        stream.getTracks().forEach((track) => {
          track.stop();
        });

        // Chrome (unlike Firefox) writes MediaRecorder webm output without a
        // duration in the header, which makes its <audio> decoder stall/play
        // silently. Patch the header so playback works cross-browser.
        let blob = rawBlob;
        if (rawBlob.type.includes("webm")) {
          try {
            blob = await fixWebmDuration(rawBlob, durationMs);
          } catch (_e) {
            blob = rawBlob;
          }
        }

        setVoiceNote({
          blob,
          url: URL.createObjectURL(blob),
          duration: Math.max(1, Math.round(durationMs / 1000)),
        });
      };

      mediaRecorderRef.current = recorder;
      recordingStartRef.current = Date.now();
      recorder.start();
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds((seconds) => {
          if (seconds + 1 >= MAX_VOICE_SECONDS) {
            mediaRecorderRef.current?.stop();
            clearInterval(recordingTimerRef.current);
            setIsRecording(false);
          }
          return seconds + 1;
        });
      }, 1000);
    } catch (_e) {
      setMicError(t("chat.massMessage.micPermissionDenied"));
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    clearInterval(recordingTimerRef.current);
    setIsRecording(false);
  };

  const discardVoiceNote = () => {
    if (voiceNote?.url) URL.revokeObjectURL(voiceNote.url);
    setVoiceNote(null);
    setRecordingSeconds(0);
    setMicError(null);
  };

  const previewFirstName = selectedAthletes[0]
    ? firstNameOf(selectedAthletes[0].label)
    : "";
  // No recipient selected yet — fall back to "client" or "athlete" to match
  // how this coach refers to their roster, rather than a generic placeholder.
  const previewFallbackName = referAsClients
    ? t("athletes.roleClientLower")
    : t("athletes.roleAthleteLower");
  const previewText = message.trim()
    ? personalizeForPreview(message, previewFirstName, previewFallbackName)
    : "";
  // Preview always resolves {first_name} using the first selected recipient
  // as a sample — the label spells that out so coaches don't read it as the
  // literal text every recipient will get.
  const previewLabelName =
    (previewFirstName || "").toUpperCase() || previewFallbackName.toUpperCase();
  const previewLabel = t("chat.massMessage.previewFor", {
    name: previewLabelName,
  });

  const canReview =
    selectedAthletes.length > 0 &&
    (message.trim().length > 0 || attachments.length > 0 || !!voiceNote);

  const closeModal = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: { "modal[type]": "" },
    });
  };

  const handleSend = async () => {
    setSending(true);
    setSendError(null);
    try {
      const formData = new FormData();
      formData.append(
        "recipients",
        JSON.stringify(selectedAthletes.map((athlete) => athlete.value))
      );
      formData.append("text", message);
      attachments.forEach(({ file }) => {
        formData.append("attachments", file, file.name);
      });
      if (voiceNote) {
        const extension = voiceNote.blob.type.includes("mp4") ? "m4a" : "webm";
        formData.append("voiceNote", voiceNote.blob, `voice-note.${extension}`);
        formData.append("voiceDuration", String(voiceNote.duration));
      }

      const response = await fetch("/actions/chat/mass-message", {
        method: "POST",
        credentials: "same-origin",
        body: formData,
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.success === false) {
        // msb-api guarantees data.error is always a short, sanitized, user-safe message (see
        // msb-api lib/api/chat/send-mass-message.js + lib/middleware/error.js) — safe to show.
        setSendError(data.error || t("chat.massMessage.sendError"));
        return;
      }
      closeModal();
    } catch (e) {
      // Anything landing here is NOT a server-reported error — a network failure, a JSON
      // parse failure, or an unexpected runtime/browser error. Never render e.message
      // verbatim; it isn't written for end users.
      console.error(e);
      setSendError(t("chat.massMessage.sendError"));
    } finally {
      setSending(false);
    }
  };

  const visibleConfirmChips = selectedAthletes.slice(0, MAX_CONFIRM_CHIPS);
  const remainingConfirmChips =
    selectedAthletes.length - visibleConfirmChips.length;

  return (
    <Modal
      toClear={[]}
      className="send-program-modal mass-message-modal"
      title={t("chat.massMessage.title")}
    >
      {step === "compose" ? (
        <>
          <p className="mass-message-subtitle">
            {t("chat.massMessage.subtitle")}
          </p>
          <div className="body">
            <div className="left">
              <div>
                <fieldset className="select-by-group">
                  <legend className="select-title">
                    {t("teams.teamInviteClients.selectByGroup")}
                  </legend>
                  <div className="select-group-filters">
                    <div
                      className="select-group-filter"
                      onClick={() =>
                        handleInputChange(!allAthletes, "allAthletes")
                      }
                    >
                      <img
                        src={`/img/new-icons/${
                          allAthletes ? "checked" : "unchecked"
                        }-square.svg`}
                        alt=""
                      />
                      {referAsClients
                        ? t("teams.teamInviteClients.allClients")
                        : t("teams.teamInviteClients.allAthletes")}
                    </div>
                    {allAthletesFromOrganizationOptions?.length > 0 &&
                      isAllowToViewAllOrganizationsAthletes && (
                        <div
                          className="select-group-filter"
                          onClick={() =>
                            handleInputChange(
                              !allOrganizationAthletes,
                              "allOrganizationAthletes"
                            )
                          }
                        >
                          <img
                            src={`/img/new-icons/${
                              allOrganizationAthletes ? "checked" : "unchecked"
                            }-square.svg`}
                            alt=""
                          />
                          {referAsClients
                            ? t(
                                "teams.teamInviteClients.allOrganizationClients"
                              )
                            : t(
                                "teams.teamInviteClients.allOrganizationAthletes"
                              )}
                        </div>
                      )}
                    {athletesGroups?.map((elem) => (
                      <div
                        key={elem.name}
                        className="select-group-filter"
                        onClick={() =>
                          handleInputChange(!state[elem.name], elem.name)
                        }
                      >
                        <img
                          src={`/img/new-icons/${
                            state[elem.name] ? "checked" : "unchecked"
                          }-square.svg`}
                          alt=""
                        />
                        <span
                          className="select-group-filter-label"
                          title={elem.name}
                        >
                          {elem.name}
                        </span>
                      </div>
                    ))}
                  </div>
                </fieldset>

                <section>
                  <header>
                    <input
                      type="search"
                      name="search"
                      placeholder={t("common:search")}
                      onChange={(e) => searchAthletes(e.target.value)}
                    />
                  </header>

                  <ul className="athlete-select">
                    {athletesOptions.length > 0 ? (
                      athletesOptions.map((athlete) =>
                        generateAthleteListItem(athlete)
                      )
                    ) : (
                      <div className="empty-filter-chat-state">
                        <p className="s1">
                          {t("chat.newGroupModal.noUsersFound")}
                        </p>
                      </div>
                    )}
                  </ul>
                </section>
              </div>
            </div>
            <div className="right">
              <header>
                <h3>{t("chat.massMessage.selectedAthlete")}</h3>
                <button
                  type="button"
                  data-button-type="secondary"
                  disabled={selectedAthletes.length === 0}
                  onClick={handleClearAll}
                >
                  {t("teams.teamInviteClients.clearAll")}
                </button>
              </header>
              <ul className="mb-6">
                <FilterChips
                  filters={selectedAthletes.map((athlete) => ({
                    text: athlete.label,
                    id: athlete.value,
                  }))}
                  clearFilter={(id) => {
                    setState((prev) => ({
                      ...prev,
                      selectedAthletes: prev.selectedAthletes.filter(
                        (athlete) => athlete.value !== id
                      ),
                    }));
                  }}
                />
              </ul>
            </div>
          </div>

          <div className="mass-message-composer">
            <div className="mass-message-composer-main">
              <span className="mass-message-composer-label">
                {t("chat.massMessage.messageLabel")}
              </span>
              <textarea
                ref={textareaRef}
                className="mass-message-textarea"
                placeholder={t("chat.massMessage.messagePlaceholder")}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />

              <div className="mass-message-toolbar">
                <span className="mass-message-toolbar-label">
                  {t("chat.massMessage.insertLabel")}
                </span>
                <button
                  type="button"
                  className="mass-message-toolbar-button"
                  onClick={() => insertAtCaret("{first_name}")}
                >
                  {t("chat.massMessage.firstNameButton")}
                </button>
                <div className="mass-message-emoji-wrapper">
                  <button
                    ref={emojiButtonRef}
                    type="button"
                    className="mass-message-toolbar-button"
                    onClick={() => setShowEmojiPicker((prev) => !prev)}
                  >
                    <span aria-hidden="true">🙂</span>
                    {t("chat.massMessage.emojiButton")}
                  </button>
                  {showEmojiPicker && (
                    <div
                      ref={emojiPickerRef}
                      className="mass-message-emoji-popover"
                    >
                      <EmojiPicker
                        theme={webAppTheme === "dark" ? "dark" : "light"}
                        onEmojiClick={(emojiData) => {
                          insertAtCaret(emojiData.emoji);
                        }}
                      />
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className={
                    isRecording
                      ? "mass-message-toolbar-button active recording"
                      : "mass-message-toolbar-button"
                  }
                  onClick={() => {
                    if (voiceNote) return;
                    isRecording ? stopRecording() : startRecording();
                  }}
                  disabled={!!voiceNote}
                >
                  {isRecording && (
                    <span
                      className="mass-message-recording-dot"
                      aria-hidden="true"
                    />
                  )}
                  <span aria-hidden="true">🎙️</span>
                  {isRecording
                    ? formatSeconds(recordingSeconds)
                    : t("chat.massMessage.audioButton")}
                </button>
                <label className="mass-message-toolbar-button mass-message-attach-label">
                  <SvgIcon
                    src="/img/widget-icons/attachment.svg"
                    noFilterOnLight={true}
                    alt=""
                  />
                  {t("chat.massMessage.attachButton")}
                  <input
                    type="file"
                    multiple
                    className="hidden"
                    onChange={(e) => {
                      handleAttachFiles(e.target.files);
                      e.target.value = "";
                    }}
                  />
                </label>
              </div>

              {(isRecording || voiceNote || micError) && (
                <div className="mass-message-voice-row">
                  <span
                    className={
                      isRecording
                        ? "mass-message-voice-icon recording"
                        : "mass-message-voice-icon"
                    }
                  >
                    🎙️
                  </span>
                  {micError ? (
                    <span className="mass-message-voice-error">{micError}</span>
                  ) : voiceNote ? (
                    <>
                      {/* biome-ignore lint/a11y/useMediaCaption: user-recorded voice note has no caption source */}
                      <audio
                        className="mass-message-voice-player"
                        controls
                        src={voiceNote.url}
                      />
                      <span>
                        {t("chat.massMessage.voiceMessageLabel")} ·{" "}
                        {formatSeconds(voiceNote.duration)}
                      </span>
                    </>
                  ) : (
                    <span className="mass-message-voice-status">
                      {isRecording ? (
                        <>
                          <span
                            className="mass-message-recording-dot"
                            aria-hidden="true"
                          />
                          {t("chat.massMessage.recording")}{" "}
                          {formatSeconds(recordingSeconds)}
                        </>
                      ) : (
                        t("chat.massMessage.readyToRecord")
                      )}
                    </span>
                  )}
                  {isRecording && (
                    <button
                      type="button"
                      className="mass-message-voice-stop"
                      onClick={stopRecording}
                    >
                      {t("chat.massMessage.stopRecording")}
                    </button>
                  )}
                  {(voiceNote || micError) && (
                    <button
                      type="button"
                      className="mass-message-voice-discard"
                      onClick={discardVoiceNote}
                    >
                      ×
                    </button>
                  )}
                </div>
              )}

              {attachError && (
                <div className="mass-message-attach-error">{attachError}</div>
              )}
            </div>

            {(attachments.length > 0 || voiceNote || previewText) && (
              <div className="mass-message-preview-panel">
                <span className="mass-message-preview-label">
                  {previewLabel}
                </span>
                <div className="mass-message-preview-bubble">
                  {attachments.length > 0 && (
                    <div className="mass-message-attachments">
                      {attachments.map(({ file, url }, index) => (
                        <div
                          className="mass-message-attachment"
                          key={`${file.name}-${index}`}
                        >
                          {url ? (
                            <img src={url} alt="" />
                          ) : (
                            <div className="mass-message-attachment-file-icon">
                              <SvgIcon
                                src="/img/widget-icons/attachment.svg"
                                noFilterOnLight={true}
                                alt=""
                              />
                            </div>
                          )}
                          <span className="mass-message-attachment-name">
                            {file.name}
                          </span>
                          <button
                            type="button"
                            className="mass-message-attachment-remove"
                            onClick={() => removeAttachment(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {voiceNote && (
                    <div className="mass-message-preview-voice">
                      {/* biome-ignore lint/a11y/useMediaCaption: user-recorded voice note has no caption source */}
                      <audio
                        className="mass-message-voice-player"
                        controls
                        src={voiceNote.url}
                      />
                      <span>{formatSeconds(voiceNote.duration)}</span>
                    </div>
                  )}
                  {previewText && (
                    <div className="mass-message-preview-text">
                      {previewText}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <footer className="mass-message-footer">
            {!canReview && (
              <div className="mass-message-validation-chip chip warning">
                {t("chat.massMessage.validationHint")}
              </div>
            )}
            <div className="mass-message-footer-actions">
              <button
                type="button"
                data-button-type="text-white"
                onClick={closeModal}
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                data-button-type="primary"
                disabled={!canReview}
                onClick={() => setStep("confirm")}
              >
                {t("chat.massMessage.reviewAndSend")}
              </button>
            </div>
          </footer>
        </>
      ) : (
        <div className="mass-message-confirm">
          <p className="mass-message-subtitle">
            {t("chat.massMessage.confirmSubtitle")}
          </p>

          <div className="mass-message-confirm-count">
            {selectedAthletes.length}
          </div>
          <h3 className="mass-message-confirm-heading">
            {t("chat.massMessage.sendingToCount", {
              count: selectedAthletes.length,
            })}
          </h3>
          <p className="mass-message-confirm-description">
            {t("chat.massMessage.confirmDescription")}
          </p>

          <div className="mass-message-confirm-chips">
            {visibleConfirmChips.map((athlete) => (
              <div className="mass-message-confirm-chip" key={athlete.value}>
                <img
                  src={getAvatarUrl(
                    { avatar: athlete.avatar },
                    false,
                    webAppTheme
                  )}
                  alt=""
                />
                <span>{athlete.label}</span>
              </div>
            ))}
            {remainingConfirmChips > 0 && (
              <div className="mass-message-confirm-chip mass-message-confirm-chip-more">
                <span>
                  {t("chat.massMessage.andMore", {
                    count: remainingConfirmChips,
                  })}
                </span>
              </div>
            )}
          </div>

          <div className="mass-message-confirm-preview">
            <span className="mass-message-preview-label">{previewLabel}</span>
            <div className="mass-message-preview-bubble">
              {attachments.length > 0 && (
                <div className="mass-message-attachments">
                  {attachments.map(({ file, url }, index) => (
                    <div
                      className="mass-message-attachment"
                      key={`${file.name}-${index}`}
                    >
                      {url ? (
                        <img src={url} alt="" />
                      ) : (
                        <div className="mass-message-attachment-file-icon">
                          <SvgIcon
                            src="/img/widget-icons/attachment.svg"
                            noFilterOnLight={true}
                            alt=""
                          />
                        </div>
                      )}
                      <span className="mass-message-attachment-name">
                        {file.name}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {voiceNote && (
                <div className="mass-message-preview-voice">
                  {/* biome-ignore lint/a11y/useMediaCaption: user-recorded voice note has no caption source */}
                  <audio
                    className="mass-message-voice-player"
                    controls
                    src={voiceNote.url}
                  />
                  <span>{formatSeconds(voiceNote.duration)}</span>
                </div>
              )}
              <div className="mass-message-preview-text">
                {previewText || t("chat.massMessage.attachmentOnlyPreview")}
              </div>
            </div>
          </div>

          {sendError && (
            <div className="mass-message-send-error">{sendError}</div>
          )}

          <footer className="mass-message-footer">
            <div className="mass-message-validation-chip chip warning">
              {t("chat.massMessage.notAGroupChat")}
            </div>
            <div className="mass-message-footer-actions">
              <button
                type="button"
                data-button-type="text-white"
                disabled={sending}
                onClick={() => setStep("compose")}
              >
                {t("common:cancel")}
              </button>
              <button
                type="button"
                data-button-type="primary"
                disabled={sending}
                onClick={handleSend}
              >
                {sending
                  ? t("chat.massMessage.sending")
                  : t("chat.massMessage.reviewAndSend")}
              </button>
            </div>
          </footer>
        </div>
      )}
    </Modal>
  );
};

export default MassMessageModalComponent;
